<script lang="ts">
	import type { CarResult } from './cars/types';
	import { browser } from '$app/environment';
	import { page } from '$app/state';
	import {
		CarEnergy,
		type CarEnergy$options,
		GetCarsStore,
		type GetEnergyPrice$result,
		GetEnergyPriceStore,
		ItineraryTrafficMode,
		ItineraryTravelMode,
	} from '$houdini';
	import FloatingAd from '$lib/components/advertising/FloatingAd.svelte';
	import Image from '$lib/components/atoms/image/Image.svelte';
	import CaravanToggle from '$lib/components/itinerary/options/CaravanToggle.svelte';
	import Cars from '$lib/components/itinerary/options/cars/Cars.svelte';
	import EvOptions from '$lib/components/itinerary/options/cars/ev/EvOptions.svelte';
	import DistanceSystem from '$lib/components/itinerary/options/DistanceSystem.svelte';
	import EvToggle from '$lib/components/itinerary/options/EvToggle.svelte';
	import EnergyPrice from '$lib/components/itinerary/options/motorVehicle/EnergyPrice.svelte';
	import TrafficMode from '$lib/components/itinerary/options/TrafficMode.svelte';
	import TripConstraint from '$lib/components/itinerary/options/TripConstraint.svelte';
	import { getTravelModeOptions } from '$lib/components/itinerary/tripOptions';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import SegmentControl from '$lib/components/molecules/segment-control/SegmentControl.svelte';
	import { amvAd } from '$lib/constants/advertising/fr/amvAd';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { trackFloatingAdCtaClick, trackFloatingAdDisplay } from '$lib/services/tracking/googleAnalytics/partners';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { capitalizeFirstLetter, formatToCamelCase } from '$lib/utils/formatString';
	import * as m from '$paraglide/messages';

	interface Props {
		carIdFromParams?: string;
	}

	const { carIdFromParams }: Props = $props();

	const { tripOptionsStore } = getMapLayoutContext();
	const searchParams = getSearchParamsStore();
	const { userProfileStore } = page.data;
	const isUserLoggedIn = $derived(!!page.data.userSession);

	let getCarsStore: GetCarsStore;
	let selectedCar: CarResult | undefined = $state();

	const isEV = $derived(selectedCar?.energy === CarEnergy.ELECTRIC);
	const filteredCarsList = $derived($getCarsStore?.data?.getCars.filter(car => isEV ? car.energy === CarEnergy.ELECTRIC : car.energy !== CarEnergy.ELECTRIC) ?? []);
	const isGetCarsError = $derived(Boolean($getCarsStore && $getCarsStore.errors));
	const isGetCarsLoading = $derived(!isGetCarsError && (!$getCarsStore || $getCarsStore.fetching));
	const showRegisterCar = $derived(!isUserLoggedIn || (isUserLoggedIn && !filteredCarsList.some(car => !car.isDefault)));
	const withCaravan = $derived($tripOptionsStore?.withCaravan);

	let energyPriceStore: GetEnergyPriceStore;
	const isGetMeLoading = $derived(Boolean(isUserLoggedIn && !($userProfileStore && $userProfileStore.errors) && ($userProfileStore && $userProfileStore.fetching)));
	const isEnergyPriceLoading = $derived(Boolean(!($energyPriceStore && $energyPriceStore.errors) && $energyPriceStore && $energyPriceStore.fetching));

	if (browser) {
		if ($searchParams.energyPrice) {
			$tripOptionsStore.energyPrice = $searchParams.energyPrice;
		}
		fetchCars();
		fetchEnergyPrice();
		fetchUserTripPreferences();
	}

	async function fetchCars() {
		getCarsStore ??= new GetCarsStore();

		await getCarsStore.fetch({ policy: 'NetworkOnly' });

		const cars = $getCarsStore.data?.getCars;
		if (!cars) {
			return;
		}
		const paramsCar = cars.find(car => car.id === carIdFromParams);
		const firstCar = cars[0];
		selectCar(paramsCar ?? firstCar);
	}

	async function fetchUserTripPreferences() {
		if (!isUserLoggedIn) {
			return;
		}

		await userProfileStore.fetch({ policy: 'NetworkOnly' });
		const userTripPreferences = $userProfileStore?.data?.me.tripPreferences;
		if (userTripPreferences) {
			$tripOptionsStore.tripConstraint = userTripPreferences.tripConstraint;
			$tripOptionsStore.distanceSystem = userTripPreferences.distanceSystem;
			$tripOptionsStore.currency = userTripPreferences.currency;
		}
	}

	async function fetchEnergyPrice() {
		if (!energyPriceStore) {
			energyPriceStore = new GetEnergyPriceStore();
		}

		await energyPriceStore.fetch({ policy: 'NetworkOnly' });

		const energyPriceData = $energyPriceStore?.data;

		if (!energyPriceData) {
			return;
		}

		if (!$tripOptionsStore.energyPrice) {
			const energyPrice = energyPriceData.getEnergyPrice?.[CarEnergy.GASOLINE];
			$tripOptionsStore.energyPrice
				= energyPrice && typeof energyPrice === 'object' && 'amount' in energyPrice
					? energyPrice.amount
					: undefined;
		}
	}

	function setEnergyPrice(
		energyPriceResult: GetEnergyPrice$result | null,
		energy?: CarEnergy$options,
	) {
		if (!energyPriceResult) {
			return;
		}
		const energyPrice = energyPriceResult?.getEnergyPrice?.[energy ?? CarEnergy.GASOLINE];
		$tripOptionsStore.energyPrice = energyPrice?.amount;
	}

	function selectCar(car: CarResult) {
		selectedCar = car;
		const model = car.name;

		$tripOptionsStore.car = {
			id: car.id,
			model,
			maker: car.maker,
			isDefault: car.isDefault,
			isEv: car.energy === 'ELECTRIC',
			energy: car.energy,
		};
		setEnergyPrice($energyPriceStore?.data, car.energy);
	}

	function switchToElectricVehicle(isElectric: boolean) {
		const newCar = $getCarsStore?.data?.getCars.find(car =>
			isElectric ? car.energy === CarEnergy.ELECTRIC : car.energy !== CarEnergy.ELECTRIC,
		);
		if (newCar) {
			selectCar(newCar);
		}
	}
</script>

<p class="sr-only">{m.itinerary__options__title()}</p>
<div class="flex flex-col gap-4 px-2 md:px-0">
	<SegmentControl
		label={m.itinerary__options__travel_mode()}
		options={getTravelModeOptions()}
		defaultSelectedOption={$tripOptionsStore.travelMode}
		hideTextOnMobile
		onSelectOption={(travelMode) => {
			track({
				action: 'mobility_type',
				payload: {
					action: 'selection',
					travel_mode: travelMode,
				},
			});
			$tripOptionsStore.travelMode = travelMode;
		}}
	/>
	{#if $tripOptionsStore.travelMode === ItineraryTravelMode.CAR || $tripOptionsStore.travelMode === ItineraryTravelMode.MOTORCYCLE}
		<TrafficMode
			checked={$tripOptionsStore.traffic === ItineraryTrafficMode.ALL}
			onChange={(checked) => {
				track({
					action: 'drive_with_traffic',
					payload: {
						action: checked ? 'on' : 'off',
					},
				});

				$tripOptionsStore.traffic = checked ? ItineraryTrafficMode.ALL : ItineraryTrafficMode.CLOSINGS;
			}}
		/>
		<TripConstraint loading={isGetMeLoading} />
	{/if}
	{#if $tripOptionsStore.travelMode === ItineraryTravelMode.CAR}
		<EvToggle
			onChange={(checked) => {
				track({
					action: 'drive_with_electric_vehicle',
					payload: {
						action: checked ? 'on' : 'off',
					},
				});
				$tripOptionsStore.isElectricVehicle = checked;
				switchToElectricVehicle(checked);
			}}
			checked={isEV}
		/>
		<Cars
			onlyEv={isEV}
			carsList={filteredCarsList}
			selectedCarId={selectedCar?.id}
			hasError={isGetCarsError}
			isLoading={isGetCarsLoading}
			{showRegisterCar}
			onRetry={fetchCars}
			onSelectCar={(car) => {
				track({
					action: 'car_model',
					payload: {
						action: 'selection',
						car_name: `${formatToCamelCase(car.name)}${capitalizeFirstLetter(car.energy)}`,
					},
				});
				selectCar(car);
			}}
		/>

		{#if isEV}
			<div class="mt-4">
				<EvOptions />
			</div>
		{/if}
	{/if}
	{#if $tripOptionsStore.travelMode === ItineraryTravelMode.CAR || $tripOptionsStore.travelMode === ItineraryTravelMode.MOTORCYCLE}
		<EnergyPrice
			{isEV}
			loading={isEnergyPriceLoading || isGetMeLoading}
			onCurrencyChange={() =>
				selectedCar && setEnergyPrice($energyPriceStore?.data, selectedCar.energy)}
		/>
	{/if}
	{#if $tripOptionsStore.travelMode === ItineraryTravelMode.CAR}
		<CaravanToggle
			checked={withCaravan}
			onChange={(checked) => {
				track({
					action: 'drive_with_caravan',
					payload: {
						action: checked ? 'on' : 'off',
					},
				});
				$tripOptionsStore.withCaravan = checked;
			}}
		/>
	{/if}
	<DistanceSystem loading={isGetMeLoading} />

	{#if page.data?.locale === 'fr-FR' && $tripOptionsStore.travelMode === ItineraryTravelMode.MOTORCYCLE}
		<FloatingAd
			trackingName={amvAd.trackingName}
			title={amvAd.title}
			ctaText={amvAd.ctaText}
			ctaUrl={amvAd.ctaUrl}
			icon="open-in-new"
			class="border border-solid border-neutral-weak rounded-lg overflow-hidden grid items-center p-3! gap-x-6!"
			titleClass="text-body-regular! text-coloredneutral"
			ctaClass="inline-block! mt-2! text-body-small-regular rounded-sm bg-neutral-weak py-2.5! px-2.75! text-coloredneutral! decoration-none"
			onCtaClick={() => {
				trackFloatingAdCtaClick({
					advertiser: amvAd.trackingName,
					searchParams: $searchParams,
					route: undefined,
					arrivalAddress: undefined,
					departureAddress: undefined,
					waypoints: undefined,
				});
			}}
			handleMount={() => {
				trackFloatingAdDisplay({
					advertiser: amvAd.trackingName,
					searchParams: $searchParams,
					route: undefined,
					arrivalAddress: undefined,
					departureAddress: undefined,
					waypoints: undefined,
				});
			}}
		>
			<div class="relative text-nowrap min-w-30">
				<span class="absolute h-53 w-53 bg-[#F1FDF4] rounded-full absolute top-1/2 left--20.5 -translate-y-1/2"></span>
				<Image
					src={amvAd.pictureLogo}
					alt={amvAd.altPictureLogo}
					width="56px"
					height="56px"
					class="relative"
				/>
				<Image
					src={amvAd.textLogo}
					alt={amvAd.altTextLogo}
					width="56px"
					height="16px"
					class="relative"
				/>
			</div>
		</FloatingAd>
	{/if}
</div>
